import {
  DiagnosticRemoteAssistanceCustomerDto,
  DiagnosticRemoteAssistanceVehicleDto,
} from '@services/api/api.service';
import { RemoteServiceConsentDto, RemoteServiceListItemDto } from '@services/api/sdf-remote-services-api.service';
import { createReducer, on } from '@ngrx/store';
import * as RootActions from '@redux/actions';
import * as Actions from '@redux/modules/remote-services/remote-support/actions';

export interface State {
  // --------------------------------
  // DEALER REMOTE SUPPORT
  // --------------------------------
  remoteSupportSession: {
    data: RemoteServiceListItemDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      runningOnly?: boolean;
      connectedOnly?: boolean;
      acceptedConsentOnly?: boolean;
      runningAndActivatedOnly?: boolean;
      qSearch?: string;
      startDate?: Date;
      endDate?: Date;
    };
  };
  remoteSupportSessionCompleted: {
    data: RemoteServiceListItemDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
      startDate?: Date;
      endDate?: Date;
    };
  };
  // --------------------------------
  // CUSTOMER REMOTE SUPPORT
  // --------------------------------
  customerVehicles: {
    data: DiagnosticRemoteAssistanceVehicleDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
    };
  };
  remoteSupportSessionCustomer: {
    data: DiagnosticRemoteAssistanceCustomerDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    };
    filters: {
      qSearch?: string;
      isCompleted?: boolean;
      startDate?: Date;
      endDate?: Date;
    };
  };
  status: any;
  url: string;
  customerConsent: RemoteServiceConsentDto | null;
}

export const initialState: State = {
  remoteSupportSession: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      runningOnly: false,
      connectedOnly: false,
      acceptedConsentOnly: false,
      runningAndActivatedOnly: false,
      qSearch: '',
      startDate: null,
      endDate: null,
    },
  },
  remoteSupportSessionCompleted: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
      startDate: null,
      endDate: null,
    },
  },
  customerVehicles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
    },
  },
  remoteSupportSessionCustomer: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
      isCompleted: false,
      startDate: null,
      endDate: null,
    },
  },
  status: {
    code: 'Loading',
    title: 'Loading',
    description: 'Waiting for the connection status',
    color: 'gray',
    icon: 'hourglass_bottom',
  },
  url: null,
  customerConsent: null,
};

export const selectRemoteSupportSessions = state => state.remoteSupport.remoteSupportSession;
export const selectRemoteSupportSessionsCompleted = state => state.remoteSupport.remoteSupportSessionCompleted;
export const selectCustomerVehicles = state => state.remoteSupport.customerVehicles;
export const selectRemoteSupportSessionsCustomer = state => state.remoteSupport.remoteSupportSessionCustomer;
export const selectAssistanceStatus = state => state.remoteSupport.status;
export const selectAssistanceUrl = state => state.remoteSupport.url;
export const selectAssistanceConsent = state => state.remoteSupport.customerConsent;

export const remoteSupportReducer = createReducer(
  initialState,
  on(RootActions.resetState, () => ({ ...initialState })),
  on(Actions.setRemoteSupportSessionsLoading, (state, { isLoading }) => ({
    ...state, remoteSupportSession: {
      ...state.remoteSupportSession,
      isLoading,
    },
  })),
  on(Actions.setRemoteSupportSessionsCompletedLoading, (state, { isLoading }) => ({
    ...state, remoteSupportSessionCompleted: {
      ...state.remoteSupportSessionCompleted,
      isLoading,
    },
  })),
  on(Actions.setRemoteSupportSessionsFilters, (state, { filters }) => ({
    ...state, remoteSupportSession: {
      ...state.remoteSupportSession,
      filters,
    },
  })),
  on(Actions.setRemoteSupportSessionsCompletedFilters, (state, { filters }) => ({
    ...state, remoteSupportSessionCompleted: {
      ...state.remoteSupportSessionCompleted,
      filters,
    },
  })),
  on(Actions.setRemoteSupportSessionsPaginator, (state, { paginator }) => ({
    ...state, remoteSupportSession: {
      ...state.remoteSupportSession,
      paginator,
    },
  })),
  on(Actions.setRemoteSupportSessionsCompletedPaginator, (state, { paginator }) => ({
    ...state, remoteSupportSessionCompleted: {
      ...state.remoteSupportSessionCompleted,
      paginator,
    },
  })),
  on(Actions.setRemoteSupportSessionsList, (state, { response }) => ({
    ...state, remoteSupportSession: {
      ...state.remoteSupportSession,
      data: response.remoteServices,
      totalCount: response.total,
    },
  })),
  on(Actions.setRemoteSupportSessionsCompletedList, (state, { response }) => ({
    ...state, remoteSupportSessionCompleted: {
      ...state.remoteSupportSessionCompleted,
      data: response.remoteServices,
      totalCount: response.total,
    },
  })),
  on(Actions.setRemoteSupportSessionStatus, (state, { status }) => ({
    ...state,
    status,
  })),
  on(Actions.setRemoteSupportSessionUrl, (state, { url }) => ({
    ...state,
    url,
  })),
  on(Actions.setRemoteSupportSessionConsent, (state, { consent }) => ({
    ...state,
    customerConsent: consent,
  })),
  on(Actions.destroyWsRemoteSupportSessionStatus, (state) => ({
    ...state,
    customerConsent: null,
  })),
  on(Actions.setCustomerVehiclesLoading, (state, { isLoading }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      isLoading,
    },
  })),
  on(Actions.setRemoteSupportSessionsCustomerLoading, (state, { isLoading }) => ({
    ...state, remoteSupportSessionCustomer: {
      ...state.remoteSupportSessionCustomer,
      isLoading,
    },
  })),
  on(Actions.setCustomerVehiclesFilters, (state, { filters }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      filters,
    },
  })),
  on(Actions.setRemoteSupportSessionsCustomerFilters, (state, { filters }) => ({
    ...state, remoteSupportSessionCustomer: {
      ...state.remoteSupportSessionCustomer,
      filters,
    },
  })),
  on(Actions.setCustomerVehiclesPaginator, (state, { paginator }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      paginator,
    },
  })),
  on(Actions.setRemoteSupportSessionsCustomerPaginator, (state, { paginator }) => ({
    ...state, remoteSupportSessionCustomer: {
      ...state.remoteSupportSessionCustomer,
      paginator,
    },
  })),
  on(Actions.setCustomerVehiclesList, (state, { response }) => ({
    ...state, customerVehicles: {
      ...state.customerVehicles,
      data: response.vehicles,
      totalCount: response.total,
    },
  })),
  on(Actions.setRemoteSupportSessionsCustomerList, (state, { response }) => ({
    ...state, remoteSupportSessionCustomer: {
      ...state.remoteSupportSessionCustomer,
      data: response.iMonitorRemoteSupportSessions,
      totalCount: response.total,
    },
  })),
);
